import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  getLlmJobs,
  LlmJobRow,
  LlmJobsResponse,
  LlmModelOption,
  LlmModelSource,
} from '../../api/llmJobs';
import LlmLabPanel from './LlmLabPanel';

type FamilyCard = {
  family: string;
  root: LlmJobRow;
  roles: LlmJobRow[];
};

const groupJobs = (jobs: LlmJobRow[]): FamilyCard[] => {
  const byFamily = new Map<string, LlmJobRow[]>();
  jobs.forEach((job) => {
    const key = job.family || job.id;
    const list = byFamily.get(key) || [];
    list.push(job);
    byFamily.set(key, list);
  });
  return Array.from(byFamily.entries()).map(([family, members]) => {
    const root = members.find((job) => !job.role) || members[0];
    const roles = members.filter((job) => job.role);
    return { family, root, roles };
  });
};

const modelLabel = (model: LlmModelOption): string => {
  const costs =
    model.input_cost_per_million != null && model.output_cost_per_million != null
      ? ` · $${model.input_cost_per_million}/$${model.output_cost_per_million} per M`
      : '';
  return `${model.label}${costs}`;
};

const LlmJobsPage: React.FC = () => {
  const [jobs, setJobs] = useState<LlmJobRow[]>([]);
  const [sources, setSources] = useState<LlmModelSource[]>([]);
  const [modelDrafts, setModelDrafts] = useState<Record<string, string>>({});
  const [providerDrafts, setProviderDrafts] = useState<Record<string, string>>({});
  const [pageTab, setPageTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const families = useMemo(() => groupJobs(jobs), [jobs]);
  const openRouterSource = sources.find((source) => source.id === 'openrouter');

  const applyResponse = useCallback((response: LlmJobsResponse) => {
    setJobs(response.jobs);
    setSources(response.sources || []);
    setModelDrafts(Object.fromEntries(response.jobs.map((job) => [job.id, job.model])));
    setProviderDrafts(
      Object.fromEntries(response.jobs.map((job) => [job.id, job.provider]))
    );
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      applyResponse(await getLlmJobs());
    } catch {
      setError('Could not load LLM job config.');
    } finally {
      setLoading(false);
    }
  }, [applyResponse]);

  useEffect(() => {
    void load();
  }, [load]);

  const modelsFor = (provider: string): LlmModelOption[] =>
    sources.find((source) => source.id === provider)?.models || [];

  const modelSelectItems = (
    provider: string,
    selectedModel: string,
    resolvedModel: string
  ): React.ReactNode[] => {
    const available = modelsFor(provider);
    const selected = available.find((item) => item.id === selectedModel);
    const options = selected
      ? [selected, ...available.filter((item) => item.id !== selectedModel)]
      : selectedModel
        ? [
            {
              id: selectedModel,
              label: selectedModel,
              context_length: 0,
              structured_output: true,
              provider_endpoints: [],
            },
            ...available,
          ]
        : available;
    return options.map((model) => (
      <MenuItem key={model.id} value={model.id}>
        {modelLabel(model)}
        {model.id === resolvedModel ? ' · current' : ''}
      </MenuItem>
    ));
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="h5" fontWeight={700}>
            LLM jobs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Models in production come from backend/config/llm_jobs.json and change on deploy.
            This page shows the resolved model and recent spend.
          </Typography>
        </Box>

        <Tabs value={pageTab} onChange={(_event, value) => setPageTab(value)}>
          <Tab label="Model configuration" />
          <Tab label="LLM Lab" />
        </Tabs>

        {pageTab === 1 ? (
          <LlmLabPanel openRouterSource={openRouterSource} />
        ) : (
          <Stack spacing={2}>
            <Alert severity="success">
              <strong>OpenRouter protected mode:</strong> explicitly tagged North
              American provider endpoints, Zero Data Retention, and data collection
              denial are enforced by the backend on every OpenRouter call. Direct
              Google/OpenAI sources follow their own provider policies.
            </Alert>
            <Alert severity="info">
              To change a model, edit <code>backend/config/llm_jobs.json</code> and deploy.
              Omit a role id to inherit that role&apos;s family row.
            </Alert>
            {error && <Alert severity="error">{error}</Alert>}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {families.map((card) => {
                  const familyModel = modelDrafts[card.root.id] ?? card.root.model;
                  const familyProvider =
                    providerDrafts[card.root.id] ?? card.root.provider;
                  const mixed = card.roles.some(
                    (role) =>
                      (modelDrafts[role.id] ?? role.model) !== familyModel ||
                      (providerDrafts[role.id] ?? role.provider) !== familyProvider
                  );
                  const source = sources.find((item) => item.id === familyProvider);
                  return (
                    <Paper key={card.family} sx={{ p: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={1.5}
                          alignItems={{ md: 'center' }}
                        >
                          <Box sx={{ minWidth: 190 }}>
                            <Typography variant="subtitle1" fontWeight={700}>
                              {card.root.label}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {card.root.group}
                              {card.root.calls_7d
                                ? ` · $${(card.root.cost_7d_usd || 0).toFixed(4)} / 7d`
                                : ' · $0 / 7d'}
                              {card.root.calls_mtd
                                ? ` · $${(card.root.cost_mtd_usd || 0).toFixed(4)} MTD`
                                : ' · $0 MTD'}
                            </Typography>
                          </Box>
                          <TextField
                            select
                            size="small"
                            label="Source"
                            value={familyProvider}
                            disabled
                            sx={{ minWidth: 190 }}
                          >
                            {sources.map((item) => (
                              <MenuItem
                                key={item.id}
                                value={item.id}
                                disabled={!item.configured}
                              >
                                {item.label}{!item.configured ? ' (not configured)' : ''}
                              </MenuItem>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            fullWidth
                            label="Model"
                            value={familyModel}
                            disabled
                          >
                            {modelSelectItems(familyProvider, familyModel, card.root.model)}
                          </TextField>
                          <Chip
                            size="small"
                            color={familyProvider === 'openrouter' ? 'success' : 'default'}
                            label={
                              mixed
                                ? 'mixed'
                                : familyProvider === 'openrouter'
                                  ? 'North America · ZDR · no training'
                                  : source?.label || familyProvider
                            }
                          />
                        </Stack>
                        {card.roles.length > 0 && (
                          <Accordion
                            disableGutters
                            elevation={0}
                            sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}
                          >
                            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 0 }}>
                              <Typography variant="body2" color="text.secondary">
                                {mixed
                                  ? 'Custom steps — each role has its own row in llm_jobs.json'
                                  : `Shared by ${card.roles
                                      .map((role) => role.label.toLowerCase())
                                      .join(', ')}`}
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails sx={{ px: 0, pt: 0 }}>
                              <Stack spacing={1}>
                                {card.roles.map((role) => {
                                  const roleProvider =
                                    providerDrafts[role.id] ?? role.provider;
                                  const roleModel = modelDrafts[role.id] ?? role.model;
                                  return (
                                    <Stack
                                      key={role.id}
                                      direction={{ xs: 'column', md: 'row' }}
                                      spacing={1}
                                      alignItems={{ md: 'center' }}
                                    >
                                      <Typography variant="body2" sx={{ minWidth: 110 }}>
                                        {role.label}
                                      </Typography>
                                      <TextField
                                        select
                                        size="small"
                                        label="Source"
                                        value={roleProvider}
                                        disabled
                                        sx={{ minWidth: 180 }}
                                      >
                                        {sources.map((item) => (
                                          <MenuItem
                                            key={item.id}
                                            value={item.id}
                                            disabled={!item.configured}
                                          >
                                            {item.label}
                                          </MenuItem>
                                        ))}
                                      </TextField>
                                      <TextField
                                        select
                                        size="small"
                                        fullWidth
                                        label="Model"
                                        value={roleModel}
                                        disabled
                                      >
                                        {modelSelectItems(roleProvider, roleModel, role.model)}
                                      </TextField>
                                      <Chip size="small" label={role.source} />
                                    </Stack>
                                  );
                                })}
                              </Stack>
                            </AccordionDetails>
                          </Accordion>
                        )}
                      </Stack>
                    </Paper>
                  );
                })}
              </Stack>
            )}
            <Stack direction="row" spacing={1}>
              <Button onClick={() => void load()} disabled={loading}>
                Reload
              </Button>
            </Stack>
          </Stack>
        )}
      </Stack>
    </Container>
  );
};

export default LlmJobsPage;
